'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchRealtyOfferTypes } from '@eliadmin/core/src/api/realty/realty-api';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { useQuery } from '@tanstack/react-query';
import {
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ArrowRightIcon, ChevronDownIcon, RealtyTypeIcon } from './realty-header-icons';
import { buildRealtyHeaderIntentHref, REALTY_HEADER_OFFER_TYPE_IDS } from './realty-header-recipe';
import styles from './realty-header.module.css';

export const REALTY_HEADER_STICKY_CHANGE_EVENT = 'eliadmin:realty-header-sticky-change';
export const REALTY_HEADER_CATEGORIES_CHANGE_EVENT = 'eliadmin:realty-header-categories-change';

export type RealtyHeaderStickyChangeDetail = {
  sticky: boolean;
};

export type RealtyHeaderCategoriesChangeDetail = {
  open: boolean;
  typeId: number | null;
};

export type RealtyHeaderCategoriesBannerProps = {
  realtyTypes: { id: number; name: string }[];
  /** Admin-provided route for the sell intent. Falls back to the configured contact page. */
  sellHref?: string;
};

type Intent = 'sell' | 'buy' | 'rent';

type IntentAction = {
  intent: Intent;
  label: string;
  href: string;
  enabled: boolean;
};

const OPEN_DELAY_MS = 80;
const CLOSE_DELAY_MS = 160;

function normalizeSellHref(sellHref: string | undefined, fallbackHref: string) {
  const candidate = sellHref?.trim();

  if (
    !candidate ||
    candidate === '#' ||
    candidate.startsWith('#') ||
    candidate.startsWith('?') ||
    /^(?:data|javascript|vbscript):/i.test(candidate)
  ) {
    return fallbackHref;
  }

  return getSafeHref(candidate);
}

export const RealtyHeaderCategoriesBanner = ({
  realtyTypes,
  sellHref,
}: RealtyHeaderCategoriesBannerProps) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const panelId = `realty-header-categories-${useId().replaceAll(':', '')}`;
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const categoryButtonRefs = useRef(new Map<number, HTMLButtonElement>());
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openScrollPositionRef = useRef(0);
  const [activeTypeId, setActiveTypeId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const offerTypesQuery = useQuery({
    queryKey: [CacheKey.REALTY_OFFER_TYPES, locale],
    queryFn: () => fetchRealtyOfferTypes({ locale }),
  });

  const availableOfferTypeIds = useMemo(
    () => new Set(offerTypesQuery.data?.data?.map(offerType => offerType.id) ?? []),
    [offerTypesQuery.data],
  );

  const visibleRealtyTypes = useMemo(() => {
    const seen = new Set<number>();

    return realtyTypes.filter(type => {
      const isValid = Number.isFinite(type.id) && Boolean(type.name?.trim()) && !seen.has(type.id);
      if (isValid) seen.add(type.id);
      return isValid;
    });
  }, [realtyTypes]);

  const queryState = offerTypesQuery.data?.data?.length
    ? 'ready'
    : offerTypesQuery.isPending
      ? 'loading'
      : offerTypesQuery.isError
        ? 'error'
        : 'empty';

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current === null) return;
    clearTimeout(openTimerRef.current);
    openTimerRef.current = null;
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const closeImmediately = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setIsOpen(false);
    setActiveTypeId(null);
  }, [clearCloseTimer, clearOpenTimer]);

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimerRef.current = setTimeout(closeImmediately, CLOSE_DELAY_MS);
  }, [clearCloseTimer, clearOpenTimer, closeImmediately]);

  const openImmediately = useCallback(
    (typeId: number) => {
      clearOpenTimer();
      clearCloseTimer();
      openScrollPositionRef.current = window.scrollY;
      setActiveTypeId(typeId);
      setIsOpen(true);
    },
    [clearCloseTimer, clearOpenTimer],
  );

  const scheduleOpen = useCallback(
    (typeId: number) => {
      clearCloseTimer();
      setActiveTypeId(typeId);

      if (isOpen) {
        clearOpenTimer();
        return;
      }

      clearOpenTimer();
      openTimerRef.current = setTimeout(() => {
        openScrollPositionRef.current = window.scrollY;
        setIsOpen(true);
        openTimerRef.current = null;
      }, OPEN_DELAY_MS);
    },
    [clearCloseTimer, clearOpenTimer, isOpen],
  );

  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent<RealtyHeaderCategoriesChangeDetail>(REALTY_HEADER_CATEGORIES_CHANGE_EVENT, {
        detail: {
          open: isOpen,
          typeId: isOpen ? activeTypeId : null,
        },
      }),
    );
  }, [activeTypeId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      closeImmediately();
    };
    const handleFocusIn = (event: globalThis.FocusEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      scheduleClose();
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      const button =
        activeTypeId === null ? undefined : categoryButtonRefs.current.get(activeTypeId);
      closeImmediately();
      button?.focus();
    };
    const handleScroll = () => {
      if (window.scrollY >= 240 || Math.abs(window.scrollY - openScrollPositionRef.current) > 1) {
        closeImmediately();
      }
    };
    const handleResize = () => closeImmediately();
    const handleStickyChange = (event: Event) => {
      const detail = (event as CustomEvent<RealtyHeaderStickyChangeDetail>).detail;
      if (detail?.sticky) closeImmediately();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener(REALTY_HEADER_STICKY_CHANGE_EVENT, handleStickyChange);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener(REALTY_HEADER_STICKY_CHANGE_EVENT, handleStickyChange);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeTypeId, closeImmediately, isOpen, scheduleClose]);

  useEffect(() => {
    if (activeTypeId === null) return;
    if (visibleRealtyTypes.some(type => type.id === activeTypeId)) return;
    closeImmediately();
  }, [activeTypeId, closeImmediately, visibleRealtyTypes]);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [clearCloseTimer, clearOpenTimer],
  );

  const fallbackSellHref = ROUTES.CONTACT() || ROUTES.REALTIES() || '/';
  const resolvedSellHref = normalizeSellHref(sellHref, fallbackSellHref);

  const getIntentActions = useCallback(
    (typeId: number): IntentAction[] => [
      {
        intent: 'sell',
        label: t('text1395'),
        href: resolvedSellHref,
        enabled: true,
      },
      {
        intent: 'buy',
        label: t('text1396'),
        href: buildRealtyHeaderIntentHref(
          ROUTES.REALTIES() || '/',
          typeId,
          REALTY_HEADER_OFFER_TYPE_IDS.buy,
        ),
        enabled: availableOfferTypeIds.has(REALTY_HEADER_OFFER_TYPE_IDS.buy),
      },
      {
        intent: 'rent',
        label: t('text1397'),
        href: buildRealtyHeaderIntentHref(
          ROUTES.REALTIES() || '/',
          typeId,
          REALTY_HEADER_OFFER_TYPE_IDS.rent,
        ),
        enabled: availableOfferTypeIds.has(REALTY_HEADER_OFFER_TYPE_IDS.rent),
      },
    ],
    [availableOfferTypeIds, resolvedSellHref, t],
  );

  const handleCategoryClick = (typeId: number) => {
    if (isOpen && activeTypeId === typeId) {
      closeImmediately();
      return;
    }

    openImmediately(typeId);
  };

  const handleCategoryKeyDown = (event: KeyboardEvent<HTMLButtonElement>, typeId: number) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openImmediately(typeId);
      requestAnimationFrame(() => {
        dropdownRef.current?.querySelector<HTMLElement>('a:not([aria-disabled="true"])')?.focus();
      });
      return;
    }

    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const index = visibleRealtyTypes.findIndex(type => type.id === typeId);
    const lastIndex = visibleRealtyTypes.length - 1;
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? lastIndex
          : event.key === 'ArrowRight'
            ? (index + 1) % visibleRealtyTypes.length
            : (index - 1 + visibleRealtyTypes.length) % visibleRealtyTypes.length;
    const nextType = visibleRealtyTypes[nextIndex];

    if (nextType) {
      categoryButtonRefs.current.get(nextType.id)?.focus();
    }
  };

  const handleRootBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
    scheduleClose();
  };

  const activeType = visibleRealtyTypes.find(type => type.id === activeTypeId);
  const actions = activeType ? getIntentActions(activeType.id) : [];
  const stripStyle = {
    gridTemplateColumns: `repeat(${Math.max(visibleRealtyTypes.length, 1)}, minmax(0, 1fr))`,
  } satisfies CSSProperties;

  return (
    <>
      {isOpen && activeType ? (
        <div
          className={styles.overlay}
          aria-hidden="true"
          data-eliadmin-realty-header-overlay=""
          data-eli-part="category-overlay"
          onPointerDown={closeImmediately}
        />
      ) : null}

      <div
        ref={rootRef}
        className={`hidden md:block ${styles.categoriesRoot}`}
        data-eliadmin-realty-header-categories=""
        data-offer-types-state={queryState}
        onBlur={handleRootBlur}
        onPointerEnter={clearCloseTimer}
        onPointerLeave={scheduleClose}
      >
        <div className={styles.categoriesStrip} data-eli-part="category-strip" style={stripStyle}>
          {visibleRealtyTypes.map(type => {
            const isActive = isOpen && activeTypeId === type.id;

            return (
              <button
                key={type.id}
                ref={node => {
                  if (node) categoryButtonRefs.current.set(type.id, node);
                  else categoryButtonRefs.current.delete(type.id);
                }}
                id={`${panelId}-trigger-${type.id}`}
                type="button"
                className={`${styles.categoryButton}${isActive ? ` ${styles.categoryButtonActive}` : ''}`}
                aria-controls={panelId}
                aria-expanded={isActive}
                data-realty-type-id={type.id}
                onClick={() => handleCategoryClick(type.id)}
                onKeyDown={event => handleCategoryKeyDown(event, type.id)}
                onPointerEnter={() => scheduleOpen(type.id)}
              >
                <RealtyTypeIcon className={styles.categoryIcon} typeId={type.id} />
                <span>{type.name}</span>
                <ChevronDownIcon
                  className={styles.categoryChevron}
                  data-eli-part="category-chevron"
                />
              </button>
            );
          })}
        </div>

        {isOpen && activeType ? (
          <div
            ref={dropdownRef}
            id={panelId}
            role="region"
            className={styles.dropdown}
            aria-busy={offerTypesQuery.isPending}
            aria-labelledby={`${panelId}-trigger-${activeType.id}`}
            data-active-realty-type={activeType.id}
            data-eli-part="category-dropdown"
            data-offer-types-state={queryState}
            onPointerEnter={clearCloseTimer}
            onPointerLeave={scheduleClose}
          >
            {actions.map(action =>
              action.enabled ? (
                <Link
                  key={action.intent}
                  href={action.href}
                  prefetch={false}
                  className={styles.dropdownAction}
                  data-intent={action.intent}
                  onClick={closeImmediately}
                >
                  <span>{action.label}</span>
                  <ArrowRightIcon className={styles.actionArrow} />
                </Link>
              ) : (
                <span
                  key={action.intent}
                  className={`${styles.dropdownAction} pointer-events-none opacity-50`}
                  role="link"
                  aria-disabled="true"
                  data-intent={action.intent}
                  data-state={queryState}
                >
                  <span>{action.label}</span>
                  <ArrowRightIcon className={styles.actionArrow} />
                </span>
              ),
            )}
          </div>
        ) : null}
      </div>
    </>
  );
};
